import { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Paper, Typography, Button, Alert, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress,
  Divider, Stack
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Normaliza el nombre de una cabecera igual que el backend
const normCab = (h) =>
  String(h ?? '').trim().toLowerCase()
    .replace(/['"]/g, '')
    .replace(/\s*\*\s*/g, '')
    .replace(/\s*\([^)]*\)\s*/g, '')
    .replace(/\s+/g, '_')
    .trim();

// Parsea cualquier archivo (xlsx/xls/csv) y devuelve array de objetos normalizados
function parsearArchivo(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array', cellText: false, raw: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const filas = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
        // Normalizar cabeceras
        const normalizadas = filas.map(fila => {
          const obj = {};
          for (const [k, v] of Object.entries(fila)) {
            obj[normCab(k)] = String(v ?? '').trim();
          }
          return obj;
        });
        resolve(normalizadas);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Procesa una fila normalizada igual que el backend para la vista previa
function procesarFila(raw) {
  const pvp     = parseFloat(raw.pvp     || raw.precio_venta  || 0) || 0;
  const precio1 = parseFloat(raw.precio_1 || raw.precio        || pvp) || 0;
  const precio2 = parseFloat(raw.precio_2 || raw.mayorista     || 0) || 0;
  const costo   = parseFloat(raw.costo    || raw.costo_ultima_compra || 0) || 0;
  const stock   = raw.stock !== '' ? parseFloat(raw.stock) : '';
  const ivaRaw  = String(raw.iva || raw.iva_porcentaje || '').trim().replace('%', '');
  const iva     = ivaRaw === '' ? '15 (default)' : `${ivaRaw}%`;

  return {
    codigo_principal:         raw.codigo || raw.codigo_principal || '—',
    'codigo_auxiliar':        raw.codigo_auxiliar || raw.sku || raw.ean || '—',
    nombre:                   raw.nombre || raw.descripcion || '—',
    pvp:                      pvp  || '—',
    precio_1:                 precio1 || '—',
    precio_2:                 precio2 || '—',
    costo:                    costo || '—',
    stock:                    stock !== '' && !isNaN(stock) ? stock : '—',
    iva,
    categoria:                raw.categoria || raw.category || '—',
  };
}

export default function ImportarProductosCSV() {
  const [archivo, setArchivo]     = useState(null);
  const [preview, setPreview]     = useState([]);
  const [totalFilas, setTotalFilas] = useState(0);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv', 'ods', 'txt'].includes(ext)) {
      setSnackbar({ open: true, message: 'Formatos soportados: .xlsx, .xls, .csv', severity: 'error' });
      return;
    }

    setArchivo(file);
    setResultado(null);
    setPreview([]);

    try {
      const filas = await parsearArchivo(file);
      setTotalFilas(filas.length);
      const procesadas = filas
        .slice(0, 10)
        .map(procesarFila)
        .filter(f => f.nombre !== '—');
      setPreview(procesadas);
    } catch (err) {
      setSnackbar({ open: true, message: `Error al leer el archivo: ${err.message}`, severity: 'error' });
    }
  };

  const importar = async () => {
    if (!archivo) return;
    setLoading(true);
    setResultado(null);
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);
      const res = await axios.post(
        `${API_URL}/api/kardex-inventario/productos/importar-csv`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      setResultado(res.data?.data || res.data);
      setSnackbar({ open: true, message: res.data?.message || 'Importación completada', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error en la importación',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  const descargarPlantilla = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/kardex-inventario/productos/plantilla-csv`,
        { headers: getHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(`${API_URL}/api/kardex-inventario/productos/plantilla-csv`, '_blank');
    }
  };

  const COLS = Object.keys(preview[0] || {
    codigo_principal: '', 'codigo_auxiliar': '', nombre: '',
    pvp: '', precio_1: '', precio_2: '', costo: '', stock: '', iva: '', categoria: '',
  });

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Importar Productos
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Suba un archivo <strong>Excel (.xlsx / .xls)</strong> o CSV con sus productos.
          Los productos existentes (mismo código) se actualizarán; los nuevos se crearán.
        </Alert>

        {/* Instrucciones */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Columnas reconocidas:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['codigo_principal','codigo_auxiliar','nombre','pvp','precio_1','precio_2',
              'costo','stock','iva','categoria'].map(col => (
              <Chip key={col} label={col} size="small" variant="outlined" sx={{ mb: 0.5 }} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            El <strong>nombre</strong> es obligatorio. <strong>iva</strong> = 15 o 0 (si se omite se usa 15%).
            Se aceptan cabeceras en cualquier orden — el sistema las detecta por nombre.
          </Typography>
        </Paper>

        {/* Botones */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={descargarPlantilla}>
            Descargar Plantilla .xlsx
          </Button>
          <Button variant="contained" component="label" startIcon={<CloudUpload />}>
            Seleccionar Archivo
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv,.ods,.txt"
              onChange={handleFileChange}
            />
          </Button>
          {archivo && !loading && (
            <Button variant="contained" color="success" onClick={importar} size="large">
              Importar ({archivo.name})
            </Button>
          )}
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Vista previa */}
        {preview.length > 0 && !resultado && (
          <>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Vista previa (primeras 10 filas)
              </Typography>
              <Chip label={`${totalFilas} filas en total`} size="small" color="primary" />
            </Stack>
            <Alert severity="warning" sx={{ mb: 2 }} >
              Revisa que la columna <strong>iva</strong> tenga el valor correcto (15 = 15%, 0 = 0%).
              Si ves valores raros verifica los nombres de cabecera en tu archivo.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLS.map(k => (
                      <TableCell key={k} sx={{ fontWeight: 700, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                        {k}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((fila, i) => (
                    <TableRow key={i} hover>
                      {COLS.map(k => (
                        <TableCell
                          key={k}
                          sx={{
                            whiteSpace: 'nowrap',
                            color: k === 'iva' && String(fila[k]).includes('default') ? 'warning.main' : 'inherit',
                            fontWeight: k === 'iva' && String(fila[k]).includes('default') ? 600 : 400,
                          }}
                        >
                          {fila[k] ?? '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Resultado */}
        {resultado && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Resultado de la importación
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <Paper sx={{ p: 2.5, textAlign: 'center', bgcolor: '#e8f5e9', flex: 1 }}>
                <CheckCircle color="success" sx={{ fontSize: 32 }} />
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {resultado.creados || 0}
                </Typography>
                <Typography variant="body2">Productos creados</Typography>
              </Paper>
              <Paper sx={{ p: 2.5, textAlign: 'center', bgcolor: '#e3f2fd', flex: 1 }}>
                <CheckCircle color="info" sx={{ fontSize: 32 }} />
                <Typography variant="h4" fontWeight={700} color="info.main">
                  {resultado.actualizados || 0}
                </Typography>
                <Typography variant="body2">Productos actualizados</Typography>
              </Paper>
              <Paper sx={{
                p: 2.5, textAlign: 'center', flex: 1,
                bgcolor: resultado.errores?.length ? '#ffebee' : '#f5f5f5',
              }}>
                <ErrorIcon
                  color={resultado.errores?.length ? 'error' : 'disabled'}
                  sx={{ fontSize: 32 }}
                />
                <Typography
                  variant="h4" fontWeight={700}
                  color={resultado.errores?.length ? 'error.main' : 'text.secondary'}
                >
                  {resultado.errores?.length || 0}
                </Typography>
                <Typography variant="body2">Errores</Typography>
              </Paper>
            </Stack>

            {resultado.errores?.length > 0 && (
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>Errores encontrados:</Typography>
                {resultado.errores.slice(0, 20).map((err, i) => (
                  <Typography key={i} variant="body2">• Fila {err.fila}: {err.error}</Typography>
                ))}
                {resultado.errores.length > 20 && (
                  <Typography variant="body2">... y {resultado.errores.length - 20} más</Typography>
                )}
              </Alert>
            )}
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
